import { useState, useEffect, useCallback } from 'react';
import { User } from '../types';
import { USERS } from '../data/authData';
import { STORAGE_KEYS, loadFromStorage, saveToStorage } from '../utils/helpers';
import { lifecycleLogin, lifecycleLogout } from '../utils/lifecycleApi';

export function useAuth() {
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    const saved = loadFromStorage<string | null>(STORAGE_KEYS.CURRENT_USER, null);
    if (saved) return USERS.find(u => u.id === saved) || null;
    return null;
  });

  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    const user = USERS.find(u => u.email === email && u.password === password);
    if (!user) return { success: false, error: 'Invalid email or password' };
    setCurrentUser(user);
    saveToStorage(STORAGE_KEYS.CURRENT_USER, user.id);
    // Also logs into the Lifecycle Management backend with the same
    // credentials, so Lifecycle pages work without a second login screen.
    // Awaited now (was previously fire-and-forget) — that gap is exactly
    // what let someone reach a Lifecycle page before the token was saved,
    // intermittently, right after logging in. If the backend isn't deployed
    // yet or this fails, it still fails quietly — Lifecycle pages show their
    // own "not connected" state rather than blocking planner login outright.
    await lifecycleLogin(email, password);
    return { success: true };
  }, []);

  const logout = useCallback(() => {
    setCurrentUser(null);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
    lifecycleLogout();
  }, []);

  const canUseMarkup = useCallback((markupType: string): boolean => {
    if (!currentUser) return false;
    if (markupType === 'npnl') return currentUser.role === 'admin';
    if (markupType === 'custom') return ['admin', 'coo', 'md'].includes(currentUser.role);
    return true;
  }, [currentUser]);

  const refreshAuth = () => {
    const saved = loadFromStorage<string | null>(STORAGE_KEYS.CURRENT_USER, null);
    if (saved) setCurrentUser(USERS.find(u => u.id === saved) || null);
    else setCurrentUser(null);
  };
  return { currentUser, login, logout, canUseMarkup, refreshAuth };
}
